import {
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef
} from 'react';

import { HEADER_HEIGHT } from '@/core/constants/layout';

type UseScrollSpyOptions = {
	offset?: number;
	enabled?: boolean;
	debugLine?: boolean;
};

type SectionBounds = {
	id: string;
	top: number;
	bottom: number;
};

export const useScrollSpy = (
	elementIds: string[],
	onChange: (id: string) => void,
	scrollElement: RefObject<HTMLElement | null> | null = null,
	{
		offset = HEADER_HEIGHT,
		enabled = false,
		debugLine = false
	}: UseScrollSpyOptions = {}
) => {
	const sectionOffsets = useRef<SectionBounds[]>([]);
	const ticking = useRef(false);
	const lastActiveId = useRef<string | null>(null);
	const scrollSpyRef = useRef({
		enabled
	});

	const getTarget = useCallback(
		() => scrollElement?.current || window,
		[scrollElement]
	);

	const computeOffsets = useCallback(() => {
		const target = getTarget();
		const isWindow = target === window;

		const containerTop = isWindow
			? 0
			: (target as HTMLElement).getBoundingClientRect().top;
		const currentScroll = isWindow
			? window.scrollY
			: (target as HTMLElement).scrollTop;

		const offsets = elementIds.flatMap(id => {
			const el = document.getElementById(id);
			if (!el) return [];

			const rect = el.getBoundingClientRect();
			if (rect.width === 0 && rect.height === 0) return [];

			const top = rect.top - containerTop + currentScroll;
			const bottom = rect.bottom - containerTop + currentScroll;

			return [{ id, top, bottom }];
		});

		sectionOffsets.current = offsets;
	}, [elementIds, getTarget]);

	useLayoutEffect(() => {
		computeOffsets();
	}, [computeOffsets]);

	const handleScroll = useCallback(() => {
		const { enabled } = scrollSpyRef.current;
		if (!elementIds.length || !enabled) return;

		const target = getTarget();
		const isWindow = target === window;

		const scrollTop = isWindow
			? window.scrollY
			: (target as HTMLElement).scrollTop;
		const scrollHeight = isWindow
			? document.documentElement.scrollHeight
			: (target as HTMLElement).scrollHeight;
		const clientHeight = isWindow
			? window.innerHeight
			: (target as HTMLElement).clientHeight;

		const scrollPct = scrollTop / (scrollHeight - clientHeight || 1);

		const transitionStart = 0.2;
		const transitionEnd = 0.8;
		const middlePoint = 0.3;
		let effectiveScrollPct = middlePoint;

		if (scrollPct < transitionStart) {
			effectiveScrollPct = (scrollPct / transitionStart) * middlePoint;
		} else if (scrollPct >= transitionEnd) {
			const range = 1 - middlePoint;
			const normalizedScroll =
				(scrollPct - transitionEnd) / (1 - transitionEnd);
			effectiveScrollPct = middlePoint + normalizedScroll * range;
		}

		const viewportHeight = clientHeight - offset;
		const viewportIntersectionPoint =
			viewportHeight * effectiveScrollPct + offset;
		const intersectionPoint = scrollTop + viewportIntersectionPoint;

		let currentId: string | null = null;

		for (let i = 0; i < sectionOffsets.current.length; i++) {
			const { id, top, bottom } = sectionOffsets.current[i];
			if (top < intersectionPoint && intersectionPoint < bottom) {
				currentId = id;
				break;
			}
		}

		if (!currentId) {
			// If below last section, select it
			const lastSection = sectionOffsets.current.at(-1);
			if (lastSection && intersectionPoint > lastSection?.bottom) {
				currentId = lastSection?.id;
			}

			// If above first section, remove it
			const firstSection = sectionOffsets.current[0];
			if (firstSection && intersectionPoint < firstSection?.top) {
				currentId = '';
			}
		}

		if (currentId !== null && currentId !== lastActiveId.current) {
			onChange(currentId);
		}
		lastActiveId.current = currentId;

		if (debugLine) {
			const el = document.getElementById('intersection-line');
			if (el) {
				el.style.top = `${Math.floor(intersectionPoint).toString()}px`;
				el.style.backgroundColor = currentId ? 'green' : 'red';
			}
		}
	}, [elementIds.length, getTarget, offset, debugLine, onChange]);

	useEffect(() => {
		scrollSpyRef.current = {
			enabled
		};
	}, [enabled]);

	useEffect(() => {
		if (!elementIds.length) return;

		const resizeObserver = new ResizeObserver(() => {
			computeOffsets();
			handleScroll();
		});

		const mutationObserver = new MutationObserver(() => {
			computeOffsets();
			handleScroll();
		});

		elementIds.forEach(id => {
			const el = document.getElementById(id);
			if (el) {
				resizeObserver.observe(el);
				mutationObserver.observe(el, {
					attributes: true,
					attributeFilter: ['style', 'class']
				});
			}
		});

		computeOffsets();
		handleScroll();

		return () => {
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, [elementIds, computeOffsets, handleScroll]);

	useEffect(() => {
		const target = getTarget();

		if (debugLine) {
			let lineEl = document.getElementById('intersection-line');
			if (!lineEl) {
				lineEl = document.createElement('div');
				lineEl.id = 'intersection-line';
				lineEl.style.cssText =
					'background-color:red; width:100%; height:2px; position:absolute; top:0; left:0; z-index:9999;';
				(target === window
					? document.body
					: (target as HTMLElement)
				).appendChild(lineEl);
			}
		}

		const onScroll = () => {
			if (!ticking.current) {
				window.requestAnimationFrame(() => {
					handleScroll();
					ticking.current = false;
				});
				ticking.current = true;
			}
		};

		target.addEventListener('scroll', onScroll, { passive: true });

		return () => {
			target.removeEventListener('scroll', onScroll);
			if (debugLine) document.getElementById('intersection-line')?.remove();
		};
	}, [scrollElement, handleScroll, debugLine, getTarget]);
};
